import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ObjetoCatalogo } from '../model/ObjetoCatalogo';
import { SessionStorageService } from './../services/session-storage.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { map, Observable, of } from 'rxjs';
import { environment } from '../environments/environment';
import { ItemCarrito } from '../model/ItemCarrito';
import { ItemMP, ObjetoCarrito } from '../model/ObjetoCarrito';
import { OpcionEnvio } from '../model/OpcionEnvio';
import { Provincia } from '../model/Provincia';
import { Sucursal } from '../model/Sucursal';
import { OpcionPago } from '../model/OpcionPago';
import { PaginatedResult } from '../model/PaginatedResult';

@Injectable({
  providedIn: 'root'
})
export class CarritoService {

  private _carrito: BehaviorSubject<ItemCarrito[]> = new BehaviorSubject<ItemCarrito[]>([]);
  private _ultimoItem: BehaviorSubject<ObjetoCatalogo | null> = new BehaviorSubject<ObjetoCatalogo | null>(null);
  private _totalItems: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private _items: ObjetoCatalogo[] = [];
  public $totalItems = this._totalItems.asObservable();
  public $ultimoItem = this._ultimoItem.asObservable();
  public $carrito = this._carrito.asObservable();

  constructor(private http: HttpClient, private service: SessionStorageService) { }

  addItem(item: ObjetoCatalogo) {
    this._ultimoItem.next(item);
    this._items.push(item);
    this._totalItems.next(this._totalItems.getValue() + 1);
    const carrito: ItemCarrito[] = this._carrito.value;
    let indexCarrito: number = carrito.findIndex(x => x.id === item.id, 0);
    if (indexCarrito != -1) {
      carrito[indexCarrito].cantidad++;
    } else {
      carrito.push({ id: item.id, item: item, cantidad: 1, precioUnitario: item.price });
    }
    this._carrito.next(carrito);
  }

  deleteItem(item: ObjetoCatalogo) {
    this._totalItems.next(this._totalItems.getValue() - 1);
    const carrito: ItemCarrito[] = this._carrito.value;
    let indexCarrito: number = carrito.findIndex(x => x.id === item.id, 0);
    if (indexCarrito != -1) {
      if (carrito[indexCarrito].cantidad > 1) {
        carrito[indexCarrito].cantidad--;
      } else {
        carrito.splice(indexCarrito, 1);
      }
    }
    this._carrito.next(carrito);
  }

  deleteItemCarrito(item: ItemCarrito) {
    const index: number = this._items.findIndex(x => x.id === item.id, 0);
    let objCatalogo: ObjetoCatalogo | undefined;
    if (index != -1) {
      objCatalogo = this._items[index];
      this.deleteItem(objCatalogo);
    }
  }

  agregarItemCarrito(item: ItemCarrito) {
    const index: number = this._items.findIndex(x => x.id === item.id, 0);
    let objCatalogo: ObjetoCatalogo | undefined;
    if (index != -1) {
      objCatalogo = this._items[index];
      this.addItem(objCatalogo);
    }
  }

  clearCarrito(): void {
    this._totalItems.next(0);
    this._items = [];
    this._carrito.next([]);
  }

  getLastItemAdded(): ObjetoCatalogo {
    return this._items[this._items.length];
  }

  createPreference(data: { form: any; opcionEnvio: any, costoEnvio: number; codigoPago: string }): Observable<string> {
    return this.http.post<string>(`${environment.apiUrl}/mercadopago`, this.createCarrito(data));
  }

  obtenerOrdenCompraTransferencia(orden: { _id: any; email: any; }): any {
    return this.http.get<any>(`${environment.apiUrl}/orders/${orden._id}`, { params: { email: orden.email } });
  }

  crearOrdenCompraTransferencia(data: { form: any; opcionEnvio: any, costoEnvio: number; codigoPago: string }) {
    return this.http.post<string>(`${environment.apiUrl}/orders/transferencia`, this.createCarrito(data));
  }

  /**
   * Metodo que crea el body para endpoint /mercadopago
   * @param data datos del comprador e items + importes
   * @returns 
   */
  createCarrito(data: { form: any; opcionEnvio: any, costoEnvio: number; codigoPago: string }): { carrito: ObjetoCarrito } {
    const carrito = this._carrito.value;
    let items: ItemMP[] = [];
    for (let itemCarrito of carrito) {
      items.push({
        quantity: itemCarrito.cantidad,
        unit_price: itemCarrito.item.price,
        title: `${itemCarrito.item.author} - ${itemCarrito.item.title}`
      });
    }
    return {
      carrito: {
        items: items,
        costoEnvio: data.costoEnvio,
        envioCode: data.opcionEnvio.opcionEnvio.code,
        codigoPago: data.codigoPago,
        name: data.form.name,
        email: data.form.email,
        phone: data.form.phone,
        direccion: data.form.direccion,
        provincia: data.form.provincia ? `${data.form.provincia.nombre}` : '',
        sucursal: data.form.sucursal.descripcion,
      }
    };
  }

  getOpcionesEnvio(): Observable<OpcionEnvio[]> {
    return this.http.get<PaginatedResult<OpcionEnvio>>(`${environment.apiUrl}/envios/opciones`).pipe(
      map(obj => obj.items)
    );
  }

  // todo: poner en db.
  getOpcionesPago(): Observable<OpcionPago[]> {
    return of([{
      name: 'transferencia',
      texto: 'Transferencia bancaria',
      id: 2,
      code: 'transferencia'
    }, {
      name: 'mercadopago',
      texto: 'Mercadopago',
      id: 1,
      code: 'mercadopago'
    }]);
  }

  getProvincias(): Observable<Provincia[]> {
    return this.http.get<PaginatedResult<Provincia>>(`${environment.apiUrl}/envios/provincias`).pipe(
      map(obj => obj.items)
    );
  }

  getSucursales(idProvincia: number): Observable<Sucursal[]> {
    return this.http.get<any>(`${environment.apiUrl}/envios/sucursales/${idProvincia}`).pipe(
      map(obj => obj.sucursales));
  }

  finalizarCompra(data: { guid: string, paymentId: number }): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/mercadopago/success`, data);
  }

  cancelarCompra(data: { guid: string }): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/mercadopago/cancel`, data);
  }

};
